import React, { useCallback } from 'react';
import { Button, Classes, Dialog, Intent } from '@blueprintjs/core';
import { Translate } from '../../WithTranslate';
import { TLCode } from '../../../../common/enums';
import { CheckBox } from 'devextreme-react';
import moment from 'moment';

export enum DateTypeOfActivity {
  BP = 'BP',
  Duty = 'Duty',
  DutyOff = 'DutyOff',
}

export enum DutyType {
  DAY = 'DAY_SINGLE',
  NIGHT = 'NIGHT_SINGLE',
  DAY_AND_NIGHT = 'DAY_PLUS_NIGHT',
}

export interface ConflictDate {
  date: Date;
  typeOfActivity: DateTypeOfActivity;
  dutyType: DutyType | undefined;
}

interface ConflictingDatesDialogProps {
  conflictingDates: ConflictDate[];
  setDutyAssignmentDatesToDelete: (datesToDelete: Array<Date>) => void;
  setBlackPointDatesToDelete: (datesToDelete: Array<Date>) => void;
  setExemptionDatesToDelete: (datesToDelete: Array<Date>) => void;
  isOpen: boolean;
  onClose: () => void;
  translate: Translate;
}

export const ConflictingDatesDialog = ({
  isOpen,
  onClose,
  translate,
  setBlackPointDatesToDelete,
  setDutyAssignmentDatesToDelete,
  setExemptionDatesToDelete,
  conflictingDates,
}: ConflictingDatesDialogProps) => {
  const [datesToDelete, setDatesToDelete] = React.useState<Array<ConflictDate>>([]);

  const setBpDutiesAndExemptionsDatesToDelete = useCallback(
    (dates: Array<ConflictDate>) => {
      const bpDates = dates.filter((d) => d.typeOfActivity === DateTypeOfActivity.BP);
      const dutyDates = dates.filter((d) => d.typeOfActivity === DateTypeOfActivity.Duty);
      const dutyOffDates = dates.filter((d) => d.typeOfActivity === DateTypeOfActivity.DutyOff);
      setBlackPointDatesToDelete(bpDates.map((d) => d.date));
      setDutyAssignmentDatesToDelete(dutyDates.map((d) => d.date));
      setExemptionDatesToDelete(dutyOffDates.map((d) => d.date));
    },
    [setBlackPointDatesToDelete, setDutyAssignmentDatesToDelete, setExemptionDatesToDelete],
  );

  const validateDateChanges = useCallback(() => {
    setBpDutiesAndExemptionsDatesToDelete(datesToDelete);
    onClose();
  }, [datesToDelete, setBpDutiesAndExemptionsDatesToDelete, onClose]);

  const cancelAndClose = useCallback(() => {
    setDatesToDelete([]);
    onClose();
  }, [onClose, setDatesToDelete]);

  const isConflictDateToDelete = useCallback(
    (conflictDate: ConflictDate): boolean => {
      return (
        datesToDelete.find(
          (d) => d.date.getTime() === conflictDate.date.getTime() && d.typeOfActivity === conflictDate.typeOfActivity,
        ) !== undefined
      );
    },
    [datesToDelete],
  );

  const onCheckBoxChange = useCallback(
    (conflictDate: ConflictDate) => (isChecked: boolean | null) => {
      if (isChecked) {
        setDatesToDelete((oldValue) => [...oldValue, conflictDate]);
      } else {
        const temp = [...datesToDelete];
        temp.splice(
          temp.findIndex(
            (t) => t.date.getTime() === conflictDate.date.getTime() && t.typeOfActivity === conflictDate.typeOfActivity,
          ),
          1,
        );
        setDatesToDelete(temp);
      }
    },
    [datesToDelete, setDatesToDelete],
  );

  return (
    <Dialog onClose={cancelAndClose} isOpen={isOpen}>
      <div className={Classes.DIALOG_BODY}>
        <br />
        {translate(TLCode.DUTIES_Date_Conflicts_Explanation)}
        <br />
        <ul>
          {conflictingDates
            .sort((a, b) => (a.date.getTime() > b.date.getTime() ? 1 : a.date.getTime() < b.date.getTime() ? -1 : 0))
            .map((conflictDate) => {
              return (
                <li>
                  <CheckBox
                    value={isConflictDateToDelete(conflictDate)}
                    onValueChange={onCheckBoxChange(conflictDate)}
                  />{' '}
                  {translate(TLCode.DUTIES_Date_Conflict_Date_Detail, {
                    date: moment(conflictDate.date).format('DD/MM/YYYY'),
                    typeOfActivity: translate(TLCode[`DUTIES_Date_Conflict_${conflictDate.typeOfActivity}`] )
                  })} {conflictDate.dutyType ? ' (' + translate(TLCode[`DUTYASSIGNMENT_${conflictDate.dutyType}`])  + ')' : ''}
                </li>
              );
            })}
        </ul>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={validateDateChanges} intent={Intent.DANGER} disabled={datesToDelete.length === 0}>
            {translate(TLCode.GENERAL_Confirm)}
          </Button>
          <Button onClick={cancelAndClose} intent={Intent.NONE}>
            {translate(TLCode.GENERAL_Cancel)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
